'use client';

import {
	Box,
	FormControl,
	FormHelperText,
	InputLabel,
	Input,
	Link,
	Typography,
} from '@mui/material';
import styles from './Login.module.scss';
import { Button } from '../../blocks/Button';
import { BrandWordmark } from '../BrandWordmark';
import React from 'react';
import MFAModal from '../MfaModal';
import MuiModal from '../../mui/MuiModal/MuiModal';
import { Password } from '../Password';
import { useLogin } from './useLogin';

export type LoginPageProps = {
	returnTo?: string;
	impersonateUser?: string;
};

export const Login: React.FC<LoginPageProps> = ( {
	returnTo,
	impersonateUser,
} ) => {
	const {
		email,
		setEmail,
		password,
		setPassword,
		mfaToken,
		setMfaToken,
		validation,
		onValidate,
		showMfaModal,
		setShowMfaModal,
		passwordExpired,
		setPasswordExpired,
		loginLoading,
		mfaLoading,
		requestResetLoading,
		onLogin,
		onRequestReset,
		handleLogin,
	} = useLogin( { returnTo, impersonateUser } );

	return (
		<>
			<Box className={ styles.container } component='main'>
				<Box className={ styles.content }>
					<Box className={ styles.intro }>
						<BrandWordmark className={ styles.brand } />
						<Box className={ styles.copy }>
							<Typography className={ styles.heading }>
								Simple tools for extraordinary events.
							</Typography>
							<Typography className={ styles.subtext }>
								A modern contract and payment platform built specifically for
								the wedding and event industry.
							</Typography>
						</Box>
					</Box>
					<Box className={ styles.dialogue }>
						<Typography variant='h1' data-cy='welcome-back'>
							Welcome back
						</Typography>
						<Typography className={ styles.cardSubtext }>
							Enter your details to access your account.
						</Typography>
						<form className={ styles.form } onSubmit={ handleLogin } noValidate>
							<FormControl variant='standard' className={ styles.input }>
								<InputLabel htmlFor='email' shrink>
									Email Address
								</InputLabel>
								<Input
									id='email'
									data-cy='email-field'
									placeholder='you@company.com'
									slotProps={ { input: { type: 'email', inputMode: 'email' } } }
									value={ email }
									onChange={ ( e ) => setEmail( e.target.value ) }
									error={ !!validation.email }
									onBlur={ () => onValidate() }
								/>
								{ validation.email && (
									<FormHelperText className={ styles.error }>
										{ validation.email }
									</FormHelperText>
								) }
							</FormControl>
							<Password
								className={ styles.input }
								label='Password'
								labelEndAdornment={
									<Link
										data-cy='forgot-password'
										href={ `/ResetPassword?emailAddress=${ email }` }
										className={ styles.forgotPassword }
									>
										Forgot?
									</Link>
								}
								value={ password }
								variant='standard'
								onChange={ ( e ) => setPassword( e.target.value ) }
								error={ !!validation.password }
								onBlur={ () => onValidate() }
								errorText={ validation.password }
							/>
							<Button
								className={ styles.submitButton }
								data-cy='sign-in-button'
								type='submit'
								color='primary'
								variant='contained'
								loading={ loginLoading || mfaLoading }
							>
								Sign in
							</Button>
							<Typography className={ styles.noAccount }>
								Don't have an account?{ ' ' }
								<Link href='/SignUp' data-cy='sign-up-link'>
									Sign up today.
								</Link>
							</Typography>
						</form>
					</Box>
				</Box>
			</Box>
			<MFAModal
				open={ showMfaModal }
				closeModal={ () => setShowMfaModal( false ) }
				login={ onLogin }
				mfaToken={ mfaToken }
				setMfaToken={ setMfaToken }
			/>
			<MuiModal
				muiModalProps={ {
					open: passwordExpired,
					onClose: () => setPasswordExpired( false ),
				} }
				title='Password Expired'
				description='Your password has expired. Please reset your password to continue.'
				controls={
					<>
						<Button
							variant='outlined'
							onClick={ () => setPasswordExpired( false ) }
							disabled={ requestResetLoading }
						>
							Cancel
						</Button>
						<Button
							variant='contained'
							color='primary'
							onClick={ onRequestReset }
							loading={ requestResetLoading }
						>
							Reset Password
						</Button>
					</>
				}
			/>
		</>
	);
};
